import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import MyTickets from "@/pages/MyTickets";
import AdminUsers from "@/pages/AdminUsers";
import AuditLog from "@/pages/AuditLog";
import Roster from "@/pages/Roster";
import SettingsPage from "@/pages/Settings";
import TagSettings from "@/pages/TagSettings";
import SsoWelcome from "@/pages/SsoWelcome";
import AppShell from "@/components/AppShell";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500 text-sm tracking-wide">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.sso_provisioned && !user.welcome_seen) return <Navigate to="/welcome" replace />;
  const isRequester = user.role === "user" || user.role === "teacher";
  return <Navigate to={isRequester ? "/tickets" : "/dashboard"} replace />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <AuthProvider>
          <ThemeToggle />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/welcome" element={<Protected><SsoWelcome /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/tickets" element={<MyTickets />} />
              <Route path="/dashboard" element={<Protected roles={["it","maintenance","custodian","mechanic","secretary","principal","admin"]}><Dashboard /></Protected>} />
              <Route path="/roster" element={<Protected roles={["secretary","principal","admin"]}><Roster /></Protected>} />
              <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
              <Route path="/admin/audit" element={<Protected roles={["admin"]}><AuditLog /></Protected>} />
              <Route path="/admin/settings" element={<Protected roles={["admin"]}><SettingsPage /></Protected>} />
              <Route path="/admin/tags" element={<Protected roles={["admin"]}><TagSettings /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
